import React, { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { Link } from "react-router-dom";
import { useProfile } from "../context/ProfileContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import {
  FaSpinner,
  FaUsers,
  FaFilter,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCalendar,
  FaEye,
  FaIdCard,
  FaEdit,
  FaChevronUp,
  FaChevronDown,
  FaColumns,
} from "react-icons/fa";

const toLocalDateString = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const Users = () => {
  const { profile } = useProfile();
  const isSuperAdmin =
    profile?.role?.name === "SuperAdmin" ||
    profile?.role?.slug === "superadmin" ||
    profile?.email === "superadmin@udrivo.in";
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNext: false,
    hasPrev: false,
    limit: 20,
  });
  const tableScrollContainerRef = useRef(null);
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    contactInformation: true,
    registrationInfo: true,
    status: true,
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const sortConfigRef = useRef({ key: null, direction: "asc" });

  // Fetch users with pagination and filters
  const fetchUsers = async (page = 1, limit = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");

      const currentLimit = limit || pagination.limit;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: currentLimit.toString(),
      });

      if (startDate) queryParams.append("startDate", toLocalDateString(startDate));
      if (endDate) queryParams.append("endDate", toLocalDateString(endDate));
      if (search && search.trim()) queryParams.append("search", search.trim());
      if (sortConfigRef.current?.key) {
        queryParams.append("sortBy", sortConfigRef.current.key);
        queryParams.append("sortOrder", sortConfigRef.current.direction || "desc");
      }

      const response = await axiosInstance.get(
        `${API.ADMIN.USERS_LIST}?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Users API response:", response.data);

      // Handle different response structures
      const usersData = response.data.users || response.data.data?.users || response.data.data || [];

      setUsers(usersData);
      setFilteredUsers(usersData);

      // Update pagination from response (align with backend shape)
      if (response.data.pagination) {
        const pg = response.data.pagination;
        setPagination({
          currentPage: pg.page || page,
          totalPages: pg.totalPages || 1,
          totalUsers: pg.totalUsers || pg.total || usersData.length,
          hasNext: pg.hasNextPage ?? false,
          hasPrev: pg.hasPrevPage ?? false,
          limit: pg.limit || currentLimit,
        });
      } else if (response.data.data?.pagination) {
        const pg = response.data.data.pagination;
        setPagination({
          currentPage: pg.page || page,
          totalPages: pg.totalPages || 1,
          totalUsers: pg.totalUsers || pg.total || usersData.length,
          hasNext: pg.hasNextPage ?? false,
          hasPrev: pg.hasPrevPage ?? false,
          limit: pg.limit || currentLimit,
        });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1);
  }, []);

  const handleFilter = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchUsers(1);
  };

  const handlePageChange = (page) => {
    fetchUsers(page);
  };

  const handleLimitChange = (newLimit) => {
    const limit = parseInt(newLimit);
    setPagination((prev) => ({ ...prev, limit, currentPage: 1 }));
    fetchUsers(1, limit);
  };

  const handleAccountStatusChange = async (user, newStatus) => {
    try {
      await axiosInstance.patch(API.ADMIN.USER_ACCOUNT_STATUS(user._id), { accountStatus: newStatus });
      const updateOne = (list) => list.map((u) => (u._id === user._id ? { ...u, accountStatus: newStatus } : u));
      setUsers(updateOne);
      setFilteredUsers(updateOne);
      toast.success("Account status updated");
    } catch (error) {
      console.error("Error updating account status:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  const handleSortSet = (key, direction) => {
    const next = { key, direction };
    setSortConfig(next);
    sortConfigRef.current = next;
    fetchUsers(1);
  };

  const handleSortLabelClick = (key) => {
    setSortConfig((prev) => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      };
      sortConfigRef.current = next;
      fetchUsers(1);
      return next;
    });
  };

  const sortChevronBtnClass = (active) =>
    `p-0.5 rounded shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-1 ${
      active
        ? "text-green-600 bg-green-50"
        : "text-gray-400 hover:text-gray-600"
    }`;

  const renderSortArrows = (sortKey) => {
    const active = sortConfig.key === sortKey;
    const ascOn = active && sortConfig.direction === "asc";
    const descOn = active && sortConfig.direction === "desc";
    return (
      <span className="inline-flex flex-row items-center shrink-0 gap-0.5">
        <button
          type="button"
          aria-label="Sort ascending"
          aria-pressed={ascOn}
          className={sortChevronBtnClass(ascOn)}
          onClick={(e) => {
            e.stopPropagation();
            handleSortSet(sortKey, "asc");
          }}
        >
          <FaChevronUp className="w-3 h-3" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Sort descending"
          aria-pressed={descOn}
          className={sortChevronBtnClass(descOn)}
          onClick={(e) => {
            e.stopPropagation();
            handleSortSet(sortKey, "desc");
          }}
        >
          <FaChevronDown className="w-3 h-3" aria-hidden />
        </button>
      </span>
    );
  };

  const renderSortableTh = (sortKey, label) => (
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
      <div className="inline-flex items-center gap-1.5 max-w-full">
        <button
          type="button"
          onClick={() => handleSortLabelClick(sortKey)}
          className="text-left min-w-0 hover:text-gray-800"
        >
          <span className="break-words">{label}</span>
        </button>
        {renderSortArrows(sortKey)}
      </div>
    </th>
  );

  const csvHeaders = [
    { label: "User ID", key: "userId" },
    { label: "Name", key: "fullName" },
    { label: "Phone", key: "contactNumber" },
    { label: "Email", key: "email" },
    { label: "Created At", key: "createdAt" },
  ];

  // Show loading state for the entire page
  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
        <div className="flex flex-col justify-center items-center py-20">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <FaUsers className="text-6xl text-blue-500 mb-4 mx-auto" />
            <FaSpinner className="animate-spin text-3xl text-blue-500 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Loading Customer Data
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we fetch customer information...
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">
          Customer Management
        </h2>
      </div>

      {/* Enhanced Filter Section */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-lg mb-6 p-6 border border-green-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 flex-wrap">
          <div className="flex-1 min-w-[140px]">
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FaCalendar className="mr-2 text-green-500" />
              Start Date
            </label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              placeholderText="Select start date"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white"
              maxDate={new Date()}
            />
          </div>

          <div className="flex-1 min-w-[140px]">
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FaCalendar className="mr-2 text-green-500" />
              End Date
            </label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              placeholderText="Select end date"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white"
              maxDate={new Date()}
              minDate={startDate}
            />
          </div>

          <div className="flex-1 min-w-[160px]">
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FaPhone className="mr-2 text-green-500" />
              Phone or name
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleFilter()}
              placeholder="Search by phone or name"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
            />
          </div>

          <button
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg flex items-center font-medium"
            onClick={handleFilter}
          >
            <FaFilter className="mr-2" />
            Apply Filter
          </button>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex justify-end gap-2 relative px-4 pt-4">
          <button
            type="button"
            onClick={() => setShowColumnOptions((v) => !v)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50"
          >
            <FaColumns className="mr-2" />
            Columns
          </button>
          {showColumnOptions && (
            <div className="absolute right-0 top-full mt-1 py-2 px-3 bg-white border rounded-lg shadow-lg z-20 min-w-[220px]">
              {[
                ["contactInformation", "Contact Information"],
                ["registrationInfo", "Registration Info"],
                ["status", "Status"],
              ].map(([col, label]) => (
                <label
                  key={col}
                  className="flex items-center gap-2 py-1 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={visibleColumns[col]}
                    onChange={(e) =>
                      setVisibleColumns((v) => ({
                        ...v,
                        [col]: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading users...</span>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <FaUsers className="text-gray-400 text-4xl mx-auto mb-4" />
            <p className="text-gray-500">
              No users found matching your criteria
            </p>
          </div>
        ) : (
          <div
            ref={tableScrollContainerRef}
            className="overflow-x-auto overflow-y-auto max-h-[70vh]"
          >
            <table className="min-w-full">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                <tr>
                  {renderSortableTh("fullName", "User Details")}
                  {visibleColumns.contactInformation &&
                    renderSortableTh("contact", "Contact Information")}
                  {visibleColumns.registrationInfo &&
                    renderSortableTh("createdAt", "Registration Info")}
                  {visibleColumns.status &&
                    renderSortableTh("accountStatus", "Status")}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center mb-1">
                          <FaUser className="text-blue-500 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {user.fullName || "N/A"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 flex items-center">
                          <FaIdCard className="mr-1" />
                          ID: {user.code || "Not assigned"}
                        </p>
                      </div>
                    </td>
                    {visibleColumns.contactInformation && (
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <FaPhone className="text-green-500 mr-2" />
                            <span className="text-sm text-gray-900">
                              {user.phone || "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <FaEnvelope className="text-purple-500 mr-2" />
                            <span className="text-sm text-gray-600">
                              {user.email || "N/A"}
                            </span>
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.registrationInfo && (
                      <td className="px-6 py-4">
                        <div>
                          <div className="flex items-center">
                            <FaCalendar className="text-orange-500 mr-2" />
                            <span className="text-sm text-gray-900">
                              {user.createdAt
                                ? new Date(user.createdAt).toLocaleString("en-IN", {
                                    timeZone: "Asia/Kolkata",
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                : "N/A"}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Registration Date
                          </p>
                        </div>
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${(user.accountStatus || "active") === "active" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>
                          {user.accountStatus || "active"}
                        </span>
                        {isSuperAdmin && (
                          <select
                            value={user.accountStatus || "active"}
                            onChange={(e) => handleAccountStatusChange(user, e.target.value)}
                            className="ml-2 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-500"
                          >
                            <option value="active">active</option>
                            <option value="deactivated">deactivated</option>
                            <option value="suspended">suspended</option>
                            <option value="banned">banned</option>
                          </select>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <Link
                          to={`/users/detail/${user._id}`}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-150"
                          title="View Details"
                        >
                          <FaEye />
                        </Link>
                        {isSuperAdmin && (
                          <Link
                            to={`/users/edit/${user._id}`}
                            className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-50 transition-colors duration-150"
                            title="Edit User"
                          >
                            <FaEdit />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500">
              Showing {(pagination.currentPage - 1) * pagination.limit + 1} to{" "}
              {Math.min(
                pagination.currentPage * pagination.limit,
                pagination.totalUsers
              )}{" "}
              of {pagination.totalUsers} results
            </div>

            <div className="flex items-center gap-4">
              {/* Limit Selector */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Show:</label>
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              {/* Page Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {Array.from(
                    { length: Math.min(pagination.totalPages, 5) },
                    (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 border rounded text-sm transition-colors ${pageNum === pagination.currentPage
                              ? "bg-green-600 text-white border-green-600"
                              : "border-gray-300 hover:bg-gray-50"
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
